/**
 * Teljes képernyős állapotok: betöltés, címképernyő, eligazítás, szünet,
 * halál, pályavég, győzelem.
 *
 * Egyetlen `Screen` osztály, amit tartalommal töltünk — így minden képernyő
 * garantáltan ugyanúgy néz ki, és nem csúszik szét a stílus.
 *
 * Gazda: C
 */

export type ScreenAction = 'start' | 'resume' | 'restart' | 'next' | 'title'

interface ScreenContent {
  kicker?: string
  title?: string
  titleAccent?: string
  sub?: string
  action?: string
  keys?: [string, string][]
  stats?: { key: string; value: string }[]
  progress?: boolean
  variant?: 'death' | ''
}

export class Screen {
  readonly root: HTMLElement
  private bar: HTMLElement | null = null
  private onAction: (() => void) | null = null

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div')
    this.root.className = 'screen hidden'
    parent.appendChild(this.root)
  }

  show(content: ScreenContent, onAction?: () => void): void {
    this.root.className = `screen ${content.variant ?? ''}`
    this.root.replaceChildren()
    this.bar = null
    this.onAction = onAction ?? null

    if (content.kicker) this.root.appendChild(text('div', 'screen-kicker', content.kicker))

    if (content.title) {
      const h = document.createElement('h1')
      h.className = 'screen-title'
      h.textContent = content.title
      if (content.titleAccent) {
        const em = document.createElement('em')
        em.textContent = content.titleAccent
        h.appendChild(em)
      }
      this.root.appendChild(h)
    }

    this.root.appendChild(div('screen-rule'))

    if (content.sub) this.root.appendChild(text('p', 'screen-sub', content.sub))

    if (content.stats) {
      const wrap = div('screen-stats')
      for (const s of content.stats) {
        const b = div('screen-stat')
        b.appendChild(text('span', 'v', s.value))
        b.appendChild(text('span', 'k', s.key))
        wrap.appendChild(b)
      }
      this.root.appendChild(wrap)
    }

    if (content.progress) {
      const bar = div('loadbar')
      const inner = document.createElement('i')
      bar.appendChild(inner)
      this.root.appendChild(bar)
      this.bar = inner
    }

    if (content.action) this.root.appendChild(text('div', 'screen-action', content.action))

    if (content.keys) {
      const grid = div('screen-keys')
      for (const [k, v] of content.keys) {
        const b = document.createElement('b')
        b.textContent = k
        grid.appendChild(b)
        grid.appendChild(text('span', '', v))
      }
      this.root.appendChild(grid)
    }
  }

  setProgress(t: number): void {
    if (this.bar) this.bar.style.width = `${Math.round(t * 100)}%`
  }

  hide(): void {
    this.root.className = 'screen hidden'
    this.onAction = null
  }

  get visible(): boolean {
    return !this.root.classList.contains('hidden')
  }

  /** A képernyő „elsődleges gombja" (Enter / kattintás / Szóköz). */
  trigger(): boolean {
    if (!this.visible || !this.onAction) return false
    const fn = this.onAction
    // Előbb elrejtjük, hogy a kezelő nyugodtan újat mutathasson.
    this.hide()
    fn()
    return true
  }
}

// ---------------------------------------------------------------------------

const CONTROLS: [string, string][] = [
  ['W A S D', 'mozgás'],
  ['Egér', 'körbenézés'],
  ['← →  vagy  Q E', 'fordulás egér nélkül'],
  ['Bal egérgomb', 'tüzelés'],
  ['1 – 6 vagy görgő', 'fegyverváltás'],
  ['Szóköz', 'ugrás'],
  ['F vagy jobb gomb', 'ajtó, kapcsoló'],
  ['Esc', 'szünet'],
  ['F3', 'diagnosztika'],
]

export const SCREENS = {
  loading: (): ScreenContent => ({
    kicker: 'Betöltés',
    title: 'ALÁSZÁLLÁS',
    sub: 'Textúrák és hangok előállítása…',
    progress: true,
  }),

  title: (): ScreenContent => ({
    kicker: 'Földalatti kutatóbázis · 4. szint',
    title: 'ALÁSZÁLLÁS',
    titleAccent: 'A MÉLYBE',
    sub:
      'Két napja nincs jel a bázisról. A kísérleti kapu nyitva maradt, és ' +
      'átjött rajta valami. A műszerek szerint odalent már csak egyetlen ' +
      'ember mozog. Te vagy az.',
    action: 'Kattints a kezdéshez',
    keys: CONTROLS,
  }),

  paused: (): ScreenContent => ({
    kicker: 'Megállítva',
    title: 'SZÜNET',
    sub: 'Odalent minden megállt. Kattints, vagy nyomj ESC-et a folytatáshoz.',
    action: 'Folytatás',
    keys: CONTROLS,
  }),

  death: (killer: string): ScreenContent => ({
    kicker: 'Az életjelek megszűntek',
    title: 'ELESTÉL',
    titleAccent: killer.toUpperCase(),
    sub: 'Innen nem visz út vissza a felszínre. Nyomj Entert, és kezdd újra.',
    action: 'Újra',
    variant: 'death',
  }),

  levelDone: (
    name: string,
    stats: { key: string; value: string }[],
    last: boolean,
  ): ScreenContent => ({
    kicker: 'Szakasz teljesítve',
    title: name.toUpperCase(),
    sub: last
      ? 'Az utolsó zsilip is bezárult mögötted.'
      : 'A zsilip kinyílt. Lejjebb már más szabályok érvényesek.',
    stats,
    action: last ? 'Befejezés' : 'Tovább',
  }),

  victory: (stats: { key: string; value: string }[]): ScreenContent => ({
    kicker: 'A kapu bezárult',
    title: 'TÚLÉLTED',
    titleAccent: 'AZ ALÁSZÁLLÁST',
    sub:
      'A mag kihunyt, a kapu összeomlott. A felszínre vezető lift még ' +
      'működik — épp csak annyi ideig, amennyi elég.',
    stats,
    action: 'Újra az elejéről',
  }),
} as const

// ---------------------------------------------------------------------------

function div(className: string): HTMLElement {
  const e = document.createElement('div')
  e.className = className
  return e
}

function text(tag: string, className: string, content: string): HTMLElement {
  const e = document.createElement(tag)
  if (className) e.className = className
  e.textContent = content
  return e
}
